import type { FollowUpTaskStatus } from "@scs/contracts";
import { useState } from "react";
import type { FormEvent } from "react";
import type { FollowUpTask } from "../api/client.js";

const statuses: readonly FollowUpTaskStatus[] = [
  "OPEN",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED"
];

export function FollowUpPanel(props: {
  canEdit: boolean;
  onCreate: (input: { dueAt: string; title: string }) => Promise<void>;
  onUpdate: (id: string, patch: { dueAt?: string; status?: FollowUpTaskStatus; title?: string }) => Promise<void>;
  tasks: readonly FollowUpTask[];
}): React.JSX.Element {
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function create(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await props.onCreate({ dueAt: new Date(dueAt).toISOString(), title });
      setDueAt("");
      setTitle("");
    } catch {
      setError("创建跟进任务失败。");
    }
  }

  const visibleTasks = props.tasks.filter((task) =>
    (dueFrom === "" || Date.parse(task.dueAt) >= Date.parse(dueFrom)) &&
    (dueTo === "" || Date.parse(task.dueAt) <= Date.parse(dueTo))
  );

  return (
    <section aria-label="跟进任务" className="inspector-followups">
      <h2>跟进任务</h2>
      {props.canEdit ? (
        <form className="inspector-task-form" onSubmit={(event) => { void create(event); }}>
          <h3>新建任务</h3>
          <label>
            标题
            <input
              maxLength={200}
              onChange={(event) => { setTitle(event.currentTarget.value); }}
              required
              value={title}
            />
          </label>
          <label>
            到期时间
            <input
              onChange={(event) => { setDueAt(event.currentTarget.value); }}
              required
              type="datetime-local"
              value={dueAt}
            />
          </label>
          <button type="submit">创建跟进</button>
        </form>
      ) : null}
      {error === null ? null : <p role="alert">{error}</p>}
      <fieldset className="inspector-task-filter">
        <legend>按待跟进时间筛选</legend>
        <label>起始<input onChange={(event) => { setDueFrom(event.currentTarget.value); }} type="datetime-local" value={dueFrom} /></label>
        <label>截止<input onChange={(event) => { setDueTo(event.currentTarget.value); }} type="datetime-local" value={dueTo} /></label>
      </fieldset>
      {visibleTasks.length === 0 ? <p className="inspector-empty-note">当前筛选条件下没有跟进任务。</p> : null}
      <ul className="inspector-task-list">
        {visibleTasks.map((task) => <TaskRow
          canEdit={props.canEdit}
          key={task.id}
          onUpdate={props.onUpdate}
          task={task}
        />)}
      </ul>
    </section>
  );
}

function TaskRow(props: {
  canEdit: boolean;
  onUpdate: (id: string, patch: { dueAt?: string; status?: FollowUpTaskStatus; title?: string }) => Promise<void>;
  task: FollowUpTask;
}): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(props.task.title);
  const [dueAt, setDueAt] = useState(toLocalInputValue(props.task.dueAt));
  const [error, setError] = useState<string | null>(null);

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await props.onUpdate(props.task.id, { dueAt: new Date(dueAt).toISOString(), title });
      setEditing(false);
    } catch {
      setError("更新跟进任务失败，请重试。");
    }
  }

  return <li className="inspector-task-card">
    {editing ? <form aria-label={`编辑跟进 ${props.task.title}`} onSubmit={(event) => { void save(event); }}>
      <label>标题<input maxLength={200} onChange={(event) => { setTitle(event.currentTarget.value); }} required value={title} /></label>
      <label>到期时间<input onChange={(event) => { setDueAt(event.currentTarget.value); }} required type="datetime-local" value={dueAt} /></label>
      <button type="submit">保存跟进</button>
      <button onClick={() => { setEditing(false); setTitle(props.task.title); setDueAt(toLocalInputValue(props.task.dueAt)); }} type="button">取消</button>
    </form> : <>
      <strong>{props.task.title}</strong><time dateTime={props.task.dueAt}>{new Date(props.task.dueAt).toLocaleString("zh-CN")}</time>
      {props.canEdit ? <>
        <select
          aria-label={`${props.task.title} 状态`}
          onChange={(event) => { void props.onUpdate(props.task.id, { status: event.currentTarget.value as FollowUpTaskStatus }); }}
          value={props.task.status}
        >
          {statuses.map((status) => <option key={status}>{status}</option>)}
        </select>
        <button onClick={() => { setEditing(true); }} type="button">编辑跟进</button>
      </> : <span> · {props.task.status}</span>}
    </>}
    {error === null ? null : <p role="alert">{error}</p>}
  </li>;
}

function toLocalInputValue(value: string): string {
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
